import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import { FieldGroup } from '@xsolla/ui-core';
import { FileUploader } from '@xsolla/ui-uploader';

import { postCustomStyleURL, uploadMerchantFile } from '../../../api';
import { useConfig } from '../../../contexts/config';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { RouteParams } from '../../../routes/types';
import { HorizontalCenterColumnBlock } from '../../../styled';
import { formatUploadLink } from '../../Authentication/EnterpriseConnections/utils';
import { FieldBlock, FormFooter } from '../../ui';
import { FooterWrapper } from './styles';
import { Action, ActionType, WidgetCustomizationState } from './types';
import { getFileNameFromURL } from './utils';

type Props = RouteComponentProps & {
  state: WidgetCustomizationState;
  dispatch: React.Dispatch<Action>;
};

const VisualSettings = ({ match, history, state, dispatch }: Props) => {
  const { navigationPath } = usePaths();
  const intl = useIntl();
  const { merchantId } = useConfig();
  const { projectId } = match.params as RouteParams;
  const { showErrorNotify, showSuccessNotify } = useNotifications();
  const { isAgreementSigned, visualSettings, submitError, submitting } = state;

  const backToUserList = useCallback(() => {
    history.push({ pathname: navigationPath(projectId) });
  }, [projectId]);

  const customStyle = useMemo(
    () =>
      visualSettings.customStyleUrl
        ? {
            filename: getFileNameFromURL(visualSettings.customStyleUrl),
            url: visualSettings.customStyleUrl
          }
        : null,
    [visualSettings.customStyleUrl]
  );

  const uploadCustomStyle = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await uploadMerchantFile(merchantId, formData);

    if (response.file_url) {
      return { filename: file.name, url: response.file_url };
    }

    const errorMessage = intl.formatMessage({
      id: 'app.validation.custom_style.upload-error'
    });

    throw new Error(errorMessage);
  };

  const deleteCustomStyle = () =>
    new Promise<void>((resolve) => {
      dispatch({ type: ActionType.setCustomStyleUrl, payload: '' });
      resolve();
    });

  const handleSubmit = async () => {
    try {
      dispatch({ type: ActionType.submitStarted });
      await postCustomStyleURL(projectId, visualSettings.customStyleUrl);
      showSuccessNotify();
      dispatch({ type: ActionType.submitSuccess });
    } catch (error) {
      dispatch({ type: ActionType.submitFailed, payload: error.toString() });
      showErrorNotify(error);
    }
  };

  return (
    <HorizontalCenterColumnBlock>
      <FieldBlock
        title={intl.formatMessage({ id: 'widget-customization.settings.visual.title' })}
        description={intl.formatMessage({
          id: 'widget-customization.settings.visual.description'
        })}>
        <FieldGroup
          label={intl.formatMessage({
            id: 'widget-customization.settings.visual.custom-styles.title'
          })}
          size="md">
          <FileUploader
            data-test-id="wc_custom-style"
            errorMessage={submitError}
            onChange={(file) => {
              if (file && file.url) {
                dispatch({
                  type: ActionType.setCustomStyleUrl,
                  payload: formatUploadLink(file.url)
                });
              }
            }}
            placeholder={intl.formatMessage({
              id: 'widget-customization.settings.visual.custom-styles.placeholder'
            })}
            options={{
              accept: '.css',
              uploadFn: uploadCustomStyle,
              deleteFn: deleteCustomStyle
            }}
            value={customStyle}
          />
        </FieldGroup>
      </FieldBlock>

      <FooterWrapper>
        <FormFooter
          onCancel={backToUserList}
          fetching={submitting}
          onSubmit={handleSubmit}
          disabled={!isAgreementSigned || visualSettings.pristine}
          error={!!submitError}
        />
      </FooterWrapper>
    </HorizontalCenterColumnBlock>
  );
};

export default withRouter(VisualSettings);
